import { useState, useEffect, useCallback } from 'react';
import { referralService } from '@/services/referralService';
import type { Referral, ReferralTier, LeaderboardEntry } from '@/types';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';

export function useReferrals() {
  const { user } = useUnifiedAuth();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [tiers, setTiers] = useState<ReferralTier[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const [refs, tierData, lb] = await Promise.all([
        referralService.list(),
        referralService.getTiers(),
        referralService.getLeaderboard(),
      ]);
      setReferrals(refs);
      setTiers(tierData);
      setLeaderboard(lb);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading referrals');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetch(); }, [fetch]);

  const create = useCallback(async (data: {
    referred_name: string;
    referred_phone: string;
    referred_vehicle_category?: string;
    notes?: string;
  }) => {
    const ref = await referralService.create(data);
    setReferrals(prev => [ref, ...prev]);
    return ref;
  }, []);

  return { referrals, tiers, leaderboard, loading, error, refetch: fetch, create };
}
